import { TreeNode } from "@/types/tree";
import { getKeywordsForImages } from "./keywords";
import {
  getImageControlsAndInfoGroupedByNodeId,
  setImageControlsAndInfoOnNodes,
  getFutureLayerImageMetaFromNode,
  updateValueOnNode,
} from "./meta";
import { callImageService } from "../api/image-bridge";
import { treeNodeReduce } from "@/lib/tree/tree";
import { ImageControlAndInfo, UpdateMode } from "./types";
import { getOptimalImageUrl } from "./dimensions";
import { UnsplashImage } from "../api/unsplash";
import {
  shouldDownloadImageToMediaLibrary,
  downloadImageToMediaLibrary,
  getMediaInputValueFromMediaUploadResponse
} from "./media-library";

export async function setStockPhotoSearchKeywordsForImageControlsInASection(
  section: TreeNode,
  mode: UpdateMode
) {
  const keywordsForImages = (await getKeywordsForImages(section)).response;
  const groupedData = getImageControlsAndInfoGroupedByNodeId(keywordsForImages);
  setImageControlsAndInfoOnNodes(groupedData, mode, section);
}

export async function setStockPhotoSearchResultsForImageControlsInASection(
  section: TreeNode,
  mode: UpdateMode
) {
  const allImageControls = treeNodeReduce(
    (node, acc) => {
      const nodeData = getFutureLayerImageMetaFromNode(node);
      if (nodeData.length > 0) {
        acc.push({
          nodeId: node.id,
          imageControls: nodeData
        });
      }
      return acc;
    },
    section,
    [] as { nodeId: TreeNode["id"]; imageControls: ImageControlAndInfo[] }[]
  );

  for (const { nodeId, imageControls } of allImageControls) {
    const updatedImageControls = await Promise.all(
      imageControls.map(async control => {
        const searchResults = await callImageService({
          query: control.keywords[0],
          page: 1,
          per_page: 10
        });

        return {
          ...control,
          searchResults
        };
      })
    );

    updateValueOnNode(
      nodeId,
      "meta.futureLayer.images",
      updatedImageControls,
      `Updated search results for node "${nodeId}"`,
      mode,
      section
    );
  }
}

export async function setImageControlsInSectionToStockPhotoSearchResults(
  section: TreeNode,
  mode: UpdateMode
) {
  const allImageControls = treeNodeReduce(
    (node, acc) => {
      const nodeData = getFutureLayerImageMetaFromNode(node);
      if (nodeData.length > 0) {
        acc.push({
          nodeId: node.id,
          imageControls: nodeData
        });
      }
      return acc;
    },
    section,
    [] as { nodeId: TreeNode["id"]; imageControls: ImageControlAndInfo[] }[]
  );

  const imageUpdatePromises = [];

  for (const { nodeId, imageControls } of allImageControls) {
    for (const control of imageControls) {
      if (control.searchResults.length > 0) {
        const img = control.searchResults[0]; // Use the first search result

        console.log("setImageInSectionToSearchResult", nodeId, control, img);

        imageUpdatePromises.push(() => {
          return setImageInSectionToSearchResult(nodeId, control, img, mode, section);
        });
      }
    }
  }

  await Promise.all(imageUpdatePromises.map(fn => fn()));
}

export async function setImageInSectionToSearchResult(
  nodeId: TreeNode["id"],
  imageControl: ImageControlAndInfo,
  img: UnsplashImage,
  mode: UpdateMode,
  section: TreeNode
) {
  const optimalUrl = getOptimalImageUrl(img, imageControl.width);

  if (shouldDownloadImageToMediaLibrary(img)) {
    // make it load in the UI instantly by hotlinking first
    updateValueOnNode(
      nodeId,
      imageControl.controlPath,
      {
        id: -1,
        type: "external_image",
        url: optimalUrl,
        alt: img.alt_description || "",
        caption: img.description || ""
      },
      `Update image at ${imageControl.controlPath}`,
      mode,
      section
    );

    const mediaLibraryResult = await downloadImageToMediaLibrary(
      optimalUrl,
      img
    );
    if (mediaLibraryResult) {
      updateValueOnNode(
        nodeId,
        imageControl.controlPath,
        getMediaInputValueFromMediaUploadResponse(mediaLibraryResult),
        `Update image at ${imageControl.controlPath} with downloaded image`,
        mode,
        section
      );
      return;
    } else {
      console.warn(
        `Failed to download image for ${imageControl.controlPath}, falling back to external image`
      );
    }
  }

  updateValueOnNode(
    nodeId,
    imageControl.controlPath,
    {
      id: -1,
      type: "external_image",
      url: optimalUrl,
      alt: img.alt_description || "",
      caption: img.description || ""
    },
    `Update image at ${imageControl.controlPath}`,
    mode,
    section
  );
}
